<template>
  <v-card flat>
    <v-card-title
      ><v-icon class="mr-2">mdi mdi-chain</v-icon>Nostoapuvälineiden tarkastus
      <v-spacer />
    </v-card-title>
    <v-divider />
    <v-card-text>
      <h3>
        Nostoapuvälineiden tarkastus on suoritettava säännöllisesti
        turvallisuuden varmistamiseksi!
      </h3>
      <h4>
        Jokaisesta tarkastetusta nostoapuvälineestä tulee tehdä uusi merkintä
        alapuolella
      </h4>
      <div class="mt-5">
        <h4><strong>Nostoapuvälineiden tarkastuslista:</strong></h4>
        <ol class="mt-3">
          <li>
            <strong>Yleinen tarkastus</strong>
            <ul>
              <li>
                Nostoapuväline on tunnistettavissa (tunnistenumero, tyyppikilpi)
              </li>
              <li>Tarkastuspäivämäärä ja seuraava tarkastus merkitty</li>
              <li>Käyttöohjeet saatavilla tarvittaessa</li>
            </ul>
          </li>
          <li>
            <strong>Visuaalinen kunto</strong>
            <ul>
              <li>Ei näkyviä vaurioita (halkeamia, kulumia, ruostetta)</li>
              <li>Ei epämuodostumia (taipumia, vääntymiä)</li>
              <li>Ei hitsausvaurioita tai korjauksia ilman hyväksyntää</li>
              <li>Ei epäilyttäviä ääniä tai liikkeitä käytön aikana</li>
            </ul>
          </li>
          <li>
            <strong>Ketjut, vaijerit ja liinat</strong>
            <ul>
              <li>Ei rispaantumista, katkenneita säikeitä tai litistymiä</li>
              <li>Ketjuissa ei ole venymiä tai epämuodostumia</li>
              <li>
                Liinoissa ei ole leikkauksia, palovaurioita tai kemiallisia
                vaurioita
              </li>
              <li>Oikeat merkinnät (kantavuus, pituus, valmistaja)</li>
            </ul>
          </li>
          <li>
            <strong>Koukut, sakkelit ja lenkit</strong>
            <ul>
              <li>Koukut eivät ole vääntyneet tai kuluneet</li>
              <li>Turvalukot toimivat moitteettomasti</li>
              <li>Sakkelit ja lenkit ovat ehjiä ja oikean kokoisia</li>
              <li>Kierrekappaleet liikkuvat sujuvasti</li>
            </ul>
          </li>
          <li>
            <strong>Toiminnallinen tarkastus (jos soveltuu)</strong>
            <ul>
              <li>Liikkuvat osat liikkuvat esteettä</li>
              <li>Mekanismit (esim. lukot, säätimet) toimivat oikein</li>
              <li>Ei öljyvuotoja tai muita merkkejä mekaanisista vioista</li>
            </ul>
          </li>
        </ol>
      </div>
    </v-card-text>
    <v-divider />
    <v-card-text>
      <v-data-table
        :items="filteredNostoapuvalineetHuoltoData"
        :headers="nostoapuvalineetHeaders"
        :loading="loadingNostoapuvalineetHuoltoData"
      >
        <template v-slot:top>
          <v-row no-gutters>
            <v-spacer />
            <v-btn
              color="primary"
              @click="openNostoapuvalineetHuoltoDialog"
              rounded
              small
              ><v-icon>mdi mdi-plus</v-icon> Uusi tarkastus</v-btn
            >
          </v-row>
        </template>
        <template v-slot:item.form="{ item }">
          <v-chip :color="item._color" class="mb-2 mt-1" small>
            {{
              item._color === "error"
                ? "Puutteellinen"
                : item._color === "warning"
                ? "Huomioita"
                : "OK"
            }}
          </v-chip>
        </template>
        <template v-slot:item.equipment="{ item }">
          <div v-if="item.equipment" class="d-flex align-center">
            <v-avatar
              size="32"
              class="mr-2"
              v-if="item.equipment.pictureThumbnail"
            >
              <v-img :src="item.equipment.pictureThumbnail" contain></v-img>
            </v-avatar>
            <v-icon size="32" class="mr-2" v-else>mdi-toy-brick</v-icon>
            <div>
              <strong>{{ item.equipment.id }}</strong
              ><br />
              <span class="text-caption">{{ item.equipment.name }}</span>
            </div>
          </div>
          <span v-else class="text-caption grey--text">Ei valittu</span>
        </template>
        <template v-slot:item.approved="{ item }">
          <v-chip
            :color="
              item.approved === true
                ? 'success'
                : item.approved === false
                ? 'error'
                : 'grey'
            "
            class="mb-2 mt-1"
            small
          >
            {{
              item.approved === true
                ? "Hyväksytty"
                : item.approved === false
                ? "Hylätty"
                : "Ei määritelty"
            }}
          </v-chip>
        </template>
        <template v-slot:item.notes="{ item }">
          {{ item.notes }}
        </template>
        <template v-slot:item.date="{ item }">
          {{ $formatDate(item.date) }}
        </template>
        <template v-slot:item.nextInspectionDate="{ item }">
          <span v-if="item.nextInspectionDate">
            {{ $formatDate(item.nextInspectionDate) }}
          </span>
          <span v-else class="text-caption grey--text">-</span> </template
        ><template v-slot:item.actions="{ item }">
          <v-btn icon @click="editNostoapuvalineetHuolto(item)">
            <v-icon>mdi mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="item.equipment"
            icon
            @click="printInspectionTag(item)"
            :loading="item._loadingPrint"
            title="Tulosta tarkastustarra"
          >
            <v-icon>mdi mdi-printer</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>
    <v-divider />
    <v-card-text>
      <nostoapuvalineet-huolto-dialog
        ref="nostoapuvalineetHuoltoDialog"
        @close="getNostoapuvalineetHuoltoData"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import NostoapuvalineetHuoltoDialog from "@/components/NostoapuvalineetHuoltoDialog.vue";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy as orderByFirestore,
} from "firebase/firestore";
import { firestore as db } from "@/plugins/firebase.app";
import { orderBy } from "lodash";
import jsPDF from "jspdf";
import QRCodeStyling from "qr-code-styling";
import dayjs from "dayjs";

export default {
  name: "NostoapuvalineethuoltoView",
  components: {
    NostoapuvalineetHuoltoDialog,
  },
  data: () => ({
    loadingNostoapuvalineetHuoltoData: false,
    nostoapuvalineetHuoltoData: [],
    nostoapuvalineetHeaders: [
      { text: "Päivämäärä", value: "date" },
      { text: "Seuraava tarkastus", value: "nextInspectionDate" },
      { text: "Kalusto", value: "equipment" },
      { text: "Tarkastaja", value: "inspector" },
      { text: "Työpaikka", value: "worksite" },
      { text: "Tila", value: "form" },
      { text: "Hyväksytty", value: "approved" },
      { text: "Huomiot", value: "notes" },
      { text: "", value: "actions", width: 50 },
    ],
  }),
  computed: {
    filteredNostoapuvalineetHuoltoData() {
      return orderBy(
        this.nostoapuvalineetHuoltoData.map((p) => {
          p.nextInspectionDate = p.date
            ? dayjs(p.date).add(1, "year").toDate()
            : null;
          if (p.form?.some((f) => f.type === null && f.done === 0)) {
            p._color = "error";
          } else if (p.form?.some((f) => f.type === null && f.done === 1)) {
            p._color = "warning";
          } else {
            p._color = "success";
          }

          // Get approval status
          const approvalField = p.form?.find((f) => f.type === "approval");
          p.approved = approvalField?.value;

          // Get notes
          const notesField = p.form?.find((f) => f.type === "textarea");
          p.notes = notesField?.value;

          return p;
        }),
        ["date", "createdAt"],
        ["desc", "desc"]
      );
    },
  },
  methods: {
    editNostoapuvalineetHuolto(data) {
      this.$refs.nostoapuvalineetHuoltoDialog?.open(data);
    },
    openNostoapuvalineetHuoltoDialog() {
      this.$refs.nostoapuvalineetHuoltoDialog?.open();
    },
    async printInspectionTag(inspection) {
      if (!inspection.equipment || !inspection.date) {
        this.$toast("Kalusto ja päivämäärä vaaditaan", { type: "error" });
        return;
      }

      // Set loading state for this specific item
      this.$set(inspection, "_loadingPrint", true);
      try {
        const equipment = inspection.equipment;
        const inspectionDate = dayjs(inspection.date);
        const nextInspectionDate = inspectionDate.add(1, "year"); // Use equipment's fullTree for location information
        let locationInfo = "Ei sijaintia";
        if (equipment.fullTree) {
          const locationParts = equipment.fullTree
            .split(" -> ")
            .filter((part) => part.trim());
          // If there are multiple parts and one is "Noormarkun paloasema", remove it
          if (
            locationParts.length > 1 &&
            locationParts.includes("Noormarkun paloasema")
          ) {
            locationInfo = locationParts
              .filter((part) => part !== "Noormarkun paloasema")
              .join(" -> ");
          } else {
            locationInfo = equipment.fullTree;
          }
        } else if (equipment.location) {
          locationInfo = equipment.location;
        }

        // Create larger format PDF (approximately 8cm x 5cm)
        const doc = new jsPDF({
          orientation: "landscape",
          unit: "cm",
          format: [8, 5],
        });

        // Generate QR code with equipment ID only
        const qrData = equipment.id;
        const qrOptions = {
          width: 120,
          height: 120,
          type: "svg",
          data: qrData,
          margin: 1,
          qrOptions: {
            typeNumber: 0,
            mode: "Byte",
            errorCorrectionLevel: "L", // Lower error correction for more data capacity
          },
          dotsOptions: {
            color: "#000000",
            type: "square",
          },
          backgroundOptions: {
            color: "#ffffff",
          },
          cornersSquareOptions: {
            color: "#000000",
            type: "square",
          },
          cornersDotOptions: {
            color: "#000000",
            type: "square",
          },
        };
        const qrCode = new QRCodeStyling(qrOptions);
        const qrImage = await qrCode.getRawData("png");
        const arrayBuffer = await qrImage.arrayBuffer(); // Add professional styling elements
        // Define margins for consistent spacing
        const leftMargin = 0.5;
        const rightMargin = 0.5;
        const topMargin = 0.3;
        const bottomMargin = 0.3;

        // Outer border
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.04);
        doc.rect(0.1, 0.1, 7.8, 4.8); // Header section with background
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.02);
        doc.line(0.1, 0.8, 7.9, 0.8); // Header separator line// Header text
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("TARKASTUSTARRA", leftMargin, 0.5);

        // Organization name in header
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.text("Noormarkun VPK", leftMargin, 0.75); // Information section border        // Main information section
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("TARKASTUSTIEDOT", leftMargin, 1.3);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`ID: ${equipment.id}`, leftMargin, 1.65);
        doc.text(
          `Tarkastettu: ${this.$formatDate(inspectionDate.toDate())}`,
          leftMargin,
          1.95
        );
        doc.text(
          `Seuraava: ${this.$formatDate(nextInspectionDate.toDate())}`,
          leftMargin,
          2.25
        );
        doc.text(`Tarkastaja: ${inspection.inspector}`, leftMargin, 2.55);

        // QR code with border
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.02);
        doc.rect(5.4, 0.15, 2.4, 2.4); // QR code border
        doc.addImage({
          imageData: new Uint8Array(arrayBuffer),
          x: 5.5,
          y: 0.25,
          width: 2.2,
          height: 2.2,
        }); // QR code label
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.015);
        doc.line(leftMargin - 0.3, 2.7, 7.9 - rightMargin, 2.7); // Separator line

        // Equipment section header
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("KALUSTO", leftMargin, 3.05); // Equipment name with better styling
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        const equipmentName =
          equipment.name.length > 55
            ? equipment.name.substring(0, 52) + "..."
            : equipment.name;
        doc.text(equipmentName, leftMargin, 3.3); // Location section with improved styling
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        const maxWidth = 4.3; // Maximum width in cm for location text (reduced for margins)
        const locationText = `Sijainti: ${locationInfo}`;
        const wrappedLocationLines = doc.splitTextToSize(
          locationText,
          maxWidth
        );
        let locationY = 3.55;
        wrappedLocationLines.forEach((line, index) => {
          doc.text(line, leftMargin, locationY + index * 0.25);
        }); // Simple zip tie hole without label (positioned with right margin consideration)
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.03);
        doc.circle(7.8 - rightMargin, 4.5 - bottomMargin, 0.15); // Save the PDF
        doc.save(
          `tarkastustarra_${equipment.id}_${this.$formatDate(
            inspectionDate.toDate()
          )}.pdf`
        );

        this.$toast("Tarkastustarra luotu onnistuneesti", { type: "success" });
      } catch (error) {
        console.error("Error generating inspection tag:", error);
        this.$toast("Virhe tarkastustagin luomisessa", { type: "error" });
      }

      // Remove loading state
      this.$set(inspection, "_loadingPrint", false);
    },
    async getNostoapuvalineetHuoltoData() {
      this.loadingNostoapuvalineetHuoltoData = true;
      try {
        const snapshot = await getDocs(
          query(
            collection(db, "nostoapuvalineethuolto"),
            where("deleted", "==", false),
            orderByFirestore("date", "desc")
          )
        );
        this.nostoapuvalineetHuoltoData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (e) {
        console.error(e);
        this.$toast("Virhe haettaessa nostoapuvälineiden tarkastusdata", {
          type: "error",
          timeout: 3000,
        });
      }
      this.loadingNostoapuvalineetHuoltoData = false;
    },
  },
  mounted() {
    this.getNostoapuvalineetHuoltoData();
  },
};
</script>
